import React, { useState, useEffect, useCallback, useRef } from 'react';
import { List, Input, Modal, Spin, Dropdown, Button, Empty } from 'antd';
import type { InputRef } from 'antd';
import {
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getChatHistory, deleteChat, updateChat } from '@/api/chat';
import { useChatStore } from '@/store/chatStore';
import './ChatHistory.less';
import { debounce } from 'lodash';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLayoutStore } from '@/store/layoutStore';
import { showMessage } from '@/utils/message';
import { useUserStore } from '@/store/userStore';

interface ChatHistoryItem {
  id: string;
  title: string;
  createTime: string;
  type: string;
}

const ChatHistory: React.FC = () => {
  const navigate = useNavigate();
  const { id: currentChatId } = useParams<{ id: string }>();
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [chatList, setChatList] = useState<ChatHistoryItem[]>([]);
  const [activeId, setActiveId] = useState<string>();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingId, setEditingId] = useState('');
  const { shouldRefreshHistory, setShouldRefreshHistory } = useChatStore();
  const inputRef = useRef<InputRef>(null);
  const isMobile = useIsMobile();
  const { collapsed, setCollapsed } = useLayoutStore();
  const userInfo = useUserStore(state => state.userInfo);


  // 加载聊天历史
  const loadChatHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getChatHistory({
        page: 1,
        pageSize: 50,
        searchText
      });

      if (res.data.code === 0) {
        const formattedHistory = res.data.data.list.map((item: any) => ({
          ...item,
          createTime: new Date(item.createTime).toLocaleString()
        }));
        setChatList(formattedHistory);
      } else {
        showMessage.error('获取聊天历史失败');
      }
    } catch (error) {
      console.error('Fetch chat history error:', error);
      showMessage.error('获取聊天历史失败');
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  // 初始加载和刷新监听
  useEffect(() => {
    // 登录后加载聊天历史
    if (userInfo) {
      loadChatHistory();
    }
  }, [loadChatHistory, userInfo]);

  // 搜索框onChange处理
  const handleSearchChange = useCallback(
    debounce((value: string) => {
      setSearchText(value);
    }, 500),
    []
  );

  // 搜索框输入处理
  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  // 根据 URL 参数设置激活的对话
  useEffect(() => {
    if (currentChatId) {
      setActiveId(currentChatId);
    }
  }, [currentChatId]);

  useEffect(() => {
    if (shouldRefreshHistory) {
      loadChatHistory();
      setShouldRefreshHistory(false);

      // 要异步判断，否则location还是旧的
      setTimeout(() => {
        // 根据 URL 中是否存在 currentChatId 来设置激活的对话
        if (currentChatId && window.location.pathname.includes(currentChatId)) {
          setActiveId(currentChatId);
        } else {
          setActiveId('');
        }
      }, 100);
    }
  }, [shouldRefreshHistory, setShouldRefreshHistory, loadChatHistory, currentChatId]);

  // 处理历史记录点击
  const handleHistoryClick = useCallback((id: string) => {
    // 设置活动ID
    setActiveId(id);
    
    // 导航到聊天详情页
    navigate(`/chat/${id}`);
    
    // 如果在移动设备上，点击后关闭侧边栏
    if (isMobile) {
      // 如果有关闭侧边栏的函数，则调用
      setCollapsed(true);
    }
  }, [navigate, setCollapsed, isMobile]);

  // 使用快捷键（Cmd/Ctrl + K）让搜索框获得焦点
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Command/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();  // 阻止浏览器默认行为
        inputRef.current?.focus();  // 让输入框获得焦点
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // 重命名对话
  const handleSaveTitle = useCallback(async () => {
    if (!editingTitle.trim()) {
      showMessage.error('标题不能为空');
      return;
    }

    try {
      const res = await updateChat({
        id: editingId,
        title: editingTitle.trim()
      });

      if (res.data.code === 0) {
        setEditModalVisible(false);
        showMessage.success('重命名成功');
        loadChatHistory();
      } else {
        showMessage.error(res.data.message || '重命名失败');
      }
    } catch (error) {
      console.error('Rename chat error:', error);
      showMessage.error('重命名失败');
    }
  }, [editingId, editingTitle, loadChatHistory]);

  // 渲染下拉菜单项
  const getDropdownItems = useCallback((id: string) => [
    {
      key: 'rename',
      label: '重命名',
      onClick: () => {
        const chat = chatList.find(item => item.id === id);
        if (chat) {
          setEditingId(id);
          setEditingTitle(chat.title);
          setEditModalVisible(true);
        }
      }
    },
    {
      key: 'delete',
      label: '删除',
      danger: true,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onClick: async (e: any) => {
        if (e && e.domEvent) {
          e.domEvent.stopPropagation();
        }
        try {
          const res = await deleteChat(id);
          if (res.data.code === 0) {
            showMessage.success('删除成功');
            if (id === currentChatId) {
              navigate('/chat');
            }
            loadChatHistory();
          } else {
            showMessage.error(res.data.message || '删除失败');
          }
        } catch (error) {
          console.error('Delete chat error:', error);
          showMessage.error('删除失败');
        }
      }
    }
  ], [chatList, currentChatId, navigate, loadChatHistory]);

  // 渲染列表项
  const renderItem = useCallback((item: ChatHistoryItem) => (
    <List.Item
      className={`chat-history-item ${item.id === activeId ? 'active' : ''}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // setActiveId(item.id);
        handleHistoryClick(item.id);
      }}
    >
      <div className="chat-history-item-content">
        <div className="chat-history-item-title">
          {item.title}
        </div>
      </div>
      <div 
        className="chat-history-item-actions"
        onClick={(e) => e.stopPropagation()}
      >
        <Dropdown
          menu={{
            items: getDropdownItems(item.id),
            onClick: e => e.domEvent.stopPropagation()
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={e => e.stopPropagation()}
            className="more-button"
          />
        </Dropdown>
      </div>
    </List.Item>
  ), [activeId, handleHistoryClick, getDropdownItems]);

  return (
    <div className="chat-history">
      <div className="chat-history-search">
        <Input
          ref={inputRef}
          placeholder="搜索问答历史 (⌘K)"
          onChange={onInputChange}
          allowClear
          prefix={<SearchOutlined style={{ color: '#999' }} />}
        />
      </div>
      <div className="chat-history-list-container">
        <Spin spinning={loading}>
          {chatList.length > 0 ? (
            <List
              className="chat-history-list"
              dataSource={chatList}
              renderItem={renderItem}
            />
          ) : (
            <Empty description="暂无聊天记录" />
          )}
        </Spin>
      </div>


      <Modal
        title="重命名对话"
        open={editModalVisible}
        onOk={handleSaveTitle}
        onCancel={() => setEditModalVisible(false)}
        okText="确定"
        cancelText="取消"
        destroyOnHidden
      >
        <Input
          value={editingTitle}
          onChange={e => setEditingTitle(e.target.value)}
          placeholder="请输入新的标题"
          maxLength={50}
          showCount
          autoFocus
          onPressEnter={handleSaveTitle}
        />
      </Modal>
    </div>
  );
};

export default ChatHistory; 